import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import { BetState } from '@/types/Bet';
import { sportsIconMap } from '@/utils/constants';
import type { Fixture, GroupedFixtureResponse } from '@/store/api/sportsApi';
type ToastType = 'success' | 'error' | 'loading' | 'custom';

// `access-token` cookie is the real source of truth for auth state (Redux
// `state.auth` resets on reload and isn't kept in sync) — Header.tsx uses
// this same check for its guest header state.
export const isGuestUser = () => !Cookies.get('access-token');

export const isGuestUserfun = () => {
  return Boolean(!Cookies.get('access-token'));
};
export const normalizeSportName = (sport?: string) =>
  sport?.trim().toLowerCase() === 'football' ? 'soccer' : sport?.trim().toLowerCase();

export const getSportFixtureGroups = (
  fixture: GroupedFixtureResponse['fixture'] | undefined,
  sportName?: string
) => {
  const normalizedSportName = normalizeSportName(sportName);
  if (!fixture || !normalizedSportName) return {};

  const sportEntry = Object.entries(fixture).find(
    ([sport]) => normalizeSportName(sport) === normalizedSportName
  );

  return sportEntry ? { [sportEntry[0]]: sportEntry[1] } : {};
};

export const flattenFixtureGroups = (fixture: GroupedFixtureResponse['fixture']) =>
  Object.values(fixture).flatMap((competitions) => Object.values(competitions).flat());

export const sortFixturesByStartTime = (fixtures: Fixture[]) =>
  [...fixtures].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

// A fixture whose id matches its own competitionId represents the league/competition
// itself (not an individual match) — pin those to the top, keeping the existing order
// otherwise.
export const isLeagueFixture = (fixture: Fixture) => fixture.id === fixture.competitionId;

export const sortFixturesWithTopLeaguesFirst = (fixtures: Fixture[]) =>
  [...fixtures].sort((a, b) => Number(isLeagueFixture(b)) - Number(isLeagueFixture(a)));

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  } else {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'absolute';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      textArea.remove();
      return Promise.resolve(true);
    } catch (error) {
      textArea.remove();
      return Promise.resolve(false);
    }
  }
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function parseJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseJWT(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
}

export const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) {
    return 'Mobile';
  }
  return 'Desktop';
};

export const showToast = (message: string, type: ToastType = 'success'): string => {
  const id = message;

  switch (type) {
    case 'success':
      return toast.success(message, { id });

    case 'error':
      return toast.error(message, { id });

    case 'loading':
      return toast.loading(message, { id });

    case 'custom':
      return toast(message, { id });

    default:
      return toast(message, { id });
  }
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error !== 'object' || error === null) return fallback;

  const errorRecord = error as Record<string, unknown>;
  const data = errorRecord.data;
  if (typeof data === 'object' && data !== null) {
    const message = (data as Record<string, unknown>).message;
    if (typeof message === 'string' && message) return message;
  }

  return typeof errorRecord.message === 'string' && errorRecord.message
    ? errorRecord.message
    : fallback;
};

export const getSportIdByName = (name: string) => {
  switch (name) {
    case 'Cricket':
      return 4;
    case 'Football':
      return 1;
    case 'Tennis':
      return 2;
    default:
      return 4;
  }
};

export const formatMatchTime = (dateInput: string | number | Date): string => {
  if (!dateInput) return '';

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) return '';

  const datePart = date.toLocaleDateString('en-GB');
  const timePart = date.toLocaleTimeString('en-GB', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return `${datePart} ${timePart}`;
};

export const formatCompactNumber = (value?: number | string | null) => {
  const num = Number(value);
  if (!num && num !== 0) return '-';

  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
};

export const getDeltaExposure = (
  selectionId: number | string,
  marketId: number | string | undefined,
  betDetails: BetState
): number => {
  const bet = betDetails?.selectedBet;
  if (!bet || bet.marketId !== marketId) return 0;

  const isSameSelection = bet.selectionId == selectionId;
  const isBack = bet.betOn?.toLowerCase() === 'back';
  const stake = betDetails?.stake || 0;

  /* ---------- BOOKMAKER ---------- */
  if (bet.marketCategory === 'BOOKMAKER') {
    if (isSameSelection) {
      const value = ((bet.rate || 0) / 100) * stake;
      return isBack ? value : -value;
    }
    return isBack ? -stake : stake;
  }

  /* ---------- NORMAL ---------- */
  if (bet.marketType === 'NORMAL') {
    if (isSameSelection) {
      const value = ((bet.rate || 0) - 1) * stake;
      return isBack ? value : -value;
    }
    return isBack ? -stake : stake;
  }

  /* ---------- FANCY / OTHER ---------- */
  if (isSameSelection) {
    return 0;
  }

  return 0;
};

export const formatNumber = (value: number | string): string => {
  const num = Number(value);

  if (isNaN(num)) return '0';

  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
  }).format(num);
};

export function formatDateTime(
  value: string | number | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!value) return '';

  const date = new Date(value);

  // invalid date guard
  if (isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options,
  }).format(date);
}

export const getMarketRunnerStatus = (
  marketStatus?: string,
  runnerStatus?: string
): string | null => {
  const rStatus = runnerStatus?.toUpperCase();
  const mStatus = marketStatus?.toUpperCase();

  const valid = ['OPEN', 'ACTIVE', ''];

  const isRunnerActive = valid.includes(rStatus ?? '');
  const isMarketActive = valid.includes(mStatus ?? '');

  if (isRunnerActive && isMarketActive) return null;

  if (!isRunnerActive && !isMarketActive) return rStatus ?? null;

  if (!isRunnerActive) return rStatus ?? null;

  return mStatus ?? null;
};

export const getSportIcon = (sportName?: string | null): string | null => {
  if (!sportName) return null;

  return sportsIconMap[sportName] ?? null;
};

export const getPlatform = (): 'Mobile' | 'Desktop' => {
  if (typeof window === 'undefined') return 'Desktop';

  const userAgent = navigator.userAgent.toLowerCase();

  const isMobile = /iphone|ipad|ipod|android|blackberry|opera mini|iemobile|mobile/i.test(
    userAgent
  );

  return isMobile ? 'Mobile' : 'Desktop';
};

export const applyInputRules = (
  value: string,
  rules?: {
    onlyNumber?: boolean;
    onlyLowerCase?: boolean;
    onlyAlphabet?: boolean;
    noSpace?: boolean;
    maxLength?: number;
    minLength?: number;
    pattern?: RegExp;
  }
) => {
  if (!rules) return value;

  let allowedChars = '';

  if (rules.pattern) {
    const match = rules.pattern.source.match(/\[([^\]]+)\]/);
    if (match?.[1]) {
      allowedChars = match[1];
    }
  }

  if (!allowedChars) {
    if (rules.onlyNumber) allowedChars = '0-9';
    else if (rules.onlyLowerCase) allowedChars = 'a-z';
    else if (rules.onlyAlphabet) allowedChars = 'a-zA-Z';
    else {
      return applyPostProcessing(value, rules);
    }
  }

  if (!rules.noSpace) allowedChars += ' ';

  const regex = new RegExp(`[^${allowedChars}]`, 'g');
  value = value.replace(regex, '');

  return applyPostProcessing(value, rules);
};

const applyPostProcessing = (
  value: string,
  rules: {
    maxLength?: number;
  }
) => {
  if (rules.maxLength && value.length > rules.maxLength) {
    value = value.slice(0, rules.maxLength);
  }

  return value;
};
